import { concatTypedArrays, reverseBitsInBytes, buf2hex, createUint24, createUint32 } from "../util";

class StacksmashingSPIFlasher {

    PSU_EXT = 0;
    PSU_18 = 1;
    PSU_33 = 2;

    constructor() {
        this.stacksmashing_spi_flasher = true;
        this.debug = false;
        this.name = "stacksmashing's SPI Flasher";
        this.description = "The hacker's choice!";
        this.device = null;
        this.sniffer = true;
        this.ready = false;
        this.inEndpointNumber = null;
        this.outEndpointNumber = null;
        this.read_size = 256;
        this.usb_filter = [{ vendorId: 0x37de, productId: 0x2 }];

        // Indicates that the adapter can check whether the SPI bus is busy
        this.supports_idle_check = true;

        // Instead of a busy boolean and a busyPromise, use a queue:
        this.taskQueue = Promise.resolve();
    }

    /**
     * The key function that ensures sequential operation.
     * We "chain" each new task on the tail of the previous tasks in the queue.
     */
    async setBusy(task) {
        // We capture the current queue "tail"...
        const previousQueue = this.taskQueue;
        
        // ...then set the queue to be the old queue plus our new task.
        // If the old queue fails, we still want to proceed to run the new task
        // (hence the `then` with both success and error).
        this.taskQueue = previousQueue
            .catch(() => {}) // prevent rejections from blocking the queue
            .then(() => task());
        
        // Finally, we return the *new* tail (the new task's result).
        return this.taskQueue;
    }

    static async requestPort() {
        return navigator.usb.requestDevice({
            filters: [{ vendorId: 0x37de, productId: 0x2 }]
        });
    }

    async getDevice() {
        this.ready = false;
        console.log("Requesting device...");
        const device = await StacksmashingSPIFlasher.requestPort();
        this.setupDevice(device);
    }

    async setupDevice(device) {
        this.ready = false;
        try {
            this.device = device;
            console.log("Opening device...");
            await this.device.open();

            console.log("Selecting configuration...");
            await this.device.selectConfiguration(1);

            const interfaceNumber = 0; // Adjust if necessary

            console.log("Claiming interface...");
            await this.device.claimInterface(interfaceNumber);
            const interfaceInfo = this.device.configuration.interfaces[interfaceNumber];
            const alternate = interfaceInfo.alternates[0]; // Assuming alternate setting 0
            const outEndpoint = alternate.endpoints.find(e => e.direction === 'out');
            const inEndpoint = alternate.endpoints.find(e => e.direction === 'in');
            this.outEndpointNumber = outEndpoint.endpointNumber;
            this.inEndpointNumber = inEndpoint.endpointNumber;
            const cmdSetGPIO = new Uint8Array([0x2]);
            await this.device.transferOut(this.outEndpointNumber, cmdSetGPIO);
            const result = await this.usbRead(64);
            this.baudrate = await this.readBaudrate();
            this.ready = true;
        } catch(error) {
            console.error("Error setting up the device:", error);
            throw error;
        }
    }

    disconnect() {
        this.ready = false;
        if (this.device) {
            this.device.close();
            console.log("Device closed");
        }
    }

    async selectChip() {
        // If you need chip-select logic, add it here.
    }

    async usbRead(count) {
        const modulo = count % 64;
        let new_count = count;
        if (modulo !== 0) {
            new_count += (64 - modulo);
        }
        while (true) {
            const result = await this.device.transferIn(this.inEndpointNumber, new_count);
            const data = new Uint8Array(result.data.buffer);
            if (this.debug) {
                console.log("USB read of", count, new_count, result);
            }
            if (data.length === 0) {
                continue;
            }
            if (data.length !== count) {
                console.log("WEIRD TRANSFER IN RESULT", result, count, new_count);
            }
            if (this.debug) console.log("Returning:", data);
            return data;
        }
    }

    /**
     * Helper that sends a command and reads `readLength` bytes,
     * then verifies `data[0] === 0xA`. Throws otherwise.
     */
    async sendCommandAndRead(commandData, readLength) {
        await this.device.transferOut(this.outEndpointNumber, commandData);
        const data = await this.usbRead(readLength);
        if (data[0] !== 0xA) {
            console.error(data);
            throw new Error("Invalid response from device");
        }
        return data;
    }

    async pinState() {
        return await this.setBusy(async () => {
            if (!this.ready) {
                throw new Error("Device not ready. Call getDevice() first.");
            }
            const idleCommand = new Uint8Array([0x10]);
            await this.device.transferOut(this.outEndpointNumber, idleCommand);
            const data = await this.usbRead(64);
            if (data[0] !== 0x6) {
                throw new Error("Invalid response from adapter1");
            }
            if (data[1] === 0x1) {
                // line is not idle
                return 1;
            } else if (data[1] === 0x0) {
                // line is idle
                return 0;
            } else if (data[1] === 0x2) {
                // line is driven
                return 2;
            } else {
                throw new Error("Invalid response from adapter2");
            }
        });
    }

    async configurePSU(psu_state) {
        return await this.setBusy(async () => {
            const command = new Uint8Array([0x05, psu_state]);
            console.log("Writing psu state", command);
            await this.sendCommandAndRead(command, 64);
        });
    }

    async readPSU() {
        return await this.setBusy(async () => {
            const command = new Uint8Array([0x06]);
            console.log("Read psu state");
            const data = await this.sendCommandAndRead(command, 64);
            // Additional check for PSU state validity
            if (data[1] > this.PSU_33) {
                throw new Error(`Invalid PSU state ${data[1]}`);
            }
            return data[1];
        });
    }

    async readVoltage() {
        return await this.setBusy(async () => {
            const command = new Uint8Array([0x04]);
            const data = await this.sendCommandAndRead(command, 64);
            return data[1] | (data[2] << 8);
        });
    }

    async readBaudrate() {
        return await this.setBusy(async () => {
            const command = new Uint8Array([0x09]);
            const data = await this.sendCommandAndRead(command, 64);
            return data[1] | (data[2] << 8) | (data[3] << 16) | (data[4] << 24);
        });
    }

    async setBaudrate(baudrate) {
        return await this.setBusy(async () => {
            const command = new Uint8Array([0x08]);
            const encoded_speed = createUint32(baudrate);
            const full_command = concatTypedArrays(command, encoded_speed);
            await this.sendCommandAndRead(full_command, 64);
            this.baudrate = baudrate;
        });
    }

    async resetLine() {
        return await this.setBusy(async () => {
            const command = new Uint8Array([0x07]);
            await this.sendCommandAndRead(command, 64);
        });
    }

    async spiTransfer(write, read_length) {
        return await this.setBusy(async () => {
            if (!this.ready) {
                throw new Error("Device not ready. Call getDevice() first.");
            }

            
            const cb = this.sniffer ? new Uint8Array([0x14]) : new Uint8Array([0x13]);
            const wb = createUint24(write.length);
            const rb = createUint24(read_length);
            const b = concatTypedArrays(
                concatTypedArrays(
                    concatTypedArrays(cb, wb),
                    rb
                ),
                write
            );
            if (this.debug) console.log("SPIFlasherWrite", write.length, read_length, b);

            await this.device.transferOut(this.outEndpointNumber, b);
            const data = await this.usbRead(read_length + 1);
            if (this.debug) console.log("SPIFlasherRead", data);

            if (data[0] === 0xAA) {
                // Return error message from device
                const decoder = new TextDecoder("ascii");
                const error_message = decoder.decode(data.slice(1));
                alert(error_message);
                throw new Error(error_message);
            } else if (data[0] !== 0x6) {
                throw new Error("Invalid response from adapter");
            }

            // Some devices return the data read during the write,
            // unify that approach here:
            const write_buf = new Uint8Array(write.length);
            const full_data = concatTypedArrays(write_buf, data.slice(1));
            if (this.debug) console.log("Returning", full_data);
            return full_data;
        });
    }
}

export { StacksmashingSPIFlasher };
